// src/components/gestionEvento/formsAjustes/EventPagePreview.js

import React from 'react';
import PropTypes from 'prop-types'; // Importar PropTypes
import {
  Box, Typography, Avatar, IconButton, Divider, Tabs, Tab, Link,
} from '@mui/material';
import {
  Facebook as FacebookIcon, Twitter as TwitterIcon, Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon, Public as PublicIcon, Email as EmailIcon,
  Phone as PhoneIcon, Person as PersonIcon, CalendarToday as CalendarIcon, LocationOn as LocationIcon // Añadidos iconos faltantes
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles'; // Importar createTheme
// Quitar getTheme si la lógica está aquí o asegurar que getTheme devuelva un tema MUI válido
// import { getTheme } from '../../../theme';

// Función simple para crear tema (o usar tu `getTheme` si es más compleja)
const createPreviewTheme = (primaryColor, secondaryColor) => {
    try {
        return createTheme({
            palette: {
                primary: { main: primaryColor || '#1976d2' },
                secondary: { main: secondaryColor || '#dc004e' },
            },
             // Puedes añadir más personalizaciones aquí si tu getTheme lo hacía
        });
    } catch (error) {
        console.error("Error creando tema de preview, usando defaults:", error);
         return createTheme({ // Fallback a tema por defecto
            palette: {
                primary: { main: '#1976d2' },
                secondary: { main: '#dc004e' },
            },
        });
    }
};


const EventPagePreview = ({
  colorPrimario = '#1976d2',
  colorSecundario = '#dc004e',
  nombreEvento = 'Nombre del Evento',
  subtitulo = '', // Añadir subtítulo
  lugar = 'Lugar de la Competencia',
  fechaInicio = '', // Usar string vacío como default
  fechaFin = '',
  logoSrc = '',
  portadaSrc = '',
  redesSociales = {}, // Default objeto vacío
  contactos = [],   // Default array vacío
}) => {
  // Crear el tema directamente aquí o llamar a tu getTheme
  const competenciaTheme = createPreviewTheme(colorPrimario, colorSecundario);

  // Formato de fecha simple (ajusta si necesitas rango como en EventPage)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Intentar crear fecha desde YYYY-MM-DD (formato de input date)
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString; // Devolver original si no es formato esperado
    const date = new Date(parts[0], parts[1] - 1, parts[2]); // Mes es 0-indexado
    if (isNaN(date.getTime())) return dateString; // Devolver original si la fecha es inválida

    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    try {
      return date.toLocaleDateString('es-ES', options);
    } catch (e) {
      return dateString; // Fallback
    }
  };

  const redesSocialesList = [
    { name: 'facebook', icon: <FacebookIcon fontSize="small"/>, link: redesSociales?.facebook },
    { name: 'instagram', icon: <InstagramIcon fontSize="small"/>, link: redesSociales?.instagram },
    { name: 'twitter', icon: <TwitterIcon fontSize="small"/>, link: redesSociales?.twitter },
    { name: 'whatsapp', icon: <WhatsAppIcon fontSize="small"/>, link: redesSociales?.whatsapp },
    { name: 'web', icon: <PublicIcon fontSize="small"/>, link: redesSociales?.sitioWeb }, // Corregido a sitioWeb
  ].filter(red => red.link); // Filtrar las que tienen enlace

  return (
    <ThemeProvider theme={competenciaTheme}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1, // Borde más sutil
          overflow: 'hidden',
          width: '100%',
          backgroundColor: 'background.paper', // Usar color del tema
          boxShadow: 1, // Sombra sutil
          my: 2, // Margen vertical
        }}
      >
        {/* --- Portada --- */}
        <Box
          sx={{ width: '100%', height: 100, bgcolor: !portadaSrc ? colorPrimario : 'transparent', overflow: 'hidden' }} // Muestra color si no hay src
        >
          {portadaSrc ? (
            <img src={portadaSrc} alt="Portada Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colorPrimario, opacity: 0.6 }}>
                <Typography variant="caption" sx={{ color: competenciaTheme.palette.getContrastText(colorPrimario) }}>Vista Previa Portada</Typography>
            </Box>
          )}
        </Box>

        {/* --- Encabezado --- */}
        <Box
          sx={{ bgcolor: colorPrimario, color: competenciaTheme.palette.getContrastText(colorPrimario), p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }} // Padding y gap ajustados
        >
          {/* Logo */}
          <Avatar
            src={logoSrc} alt="Logo" variant="rounded"
            sx={{ width: 50, height: 50, border: '2px solid white', bgcolor: colorSecundario }}
          >
            {nombreEvento ? nombreEvento.charAt(0).toUpperCase() : 'E'}
          </Avatar>
          {/* Info Principal */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{nombreEvento}</Typography>
            {subtitulo && <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>{subtitulo}</Typography>}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                {(fechaInicio || fechaFin) && (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="inherit" sx={{ verticalAlign: 'middle', fontSize: '0.8rem' }} />
                    <Typography variant="caption">{`${formatDate(fechaInicio)} - ${formatDate(fechaFin)}`}</Typography>
                 </Box>
                )}
                {lugar && (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationIcon fontSize="inherit" sx={{ verticalAlign: 'middle', fontSize: '0.8rem' }} />
                    <Typography variant="caption">{lugar}</Typography>
                 </Box>
                )}
            </Box>
          </Box>
          {/* Redes Sociales */}
          {redesSocialesList.length > 0 && (
             <Box sx={{ display: 'flex', gap: 0.5 }}>
                {redesSocialesList.map((red) => (
                    <IconButton key={red.name} href={red.link || '#'} target="_blank" size="small" sx={{ color: 'inherit', p: 0.5 }}>
                        {red.icon}
                    </IconButton>
                ))}
             </Box>
          )}
        </Box>

        {/* --- Contenido Simulado --- */}
        <Box sx={{ p: 2 }}>
          <Typography variant="overline" color="textSecondary">Vista Previa</Typography>
          <Divider sx={{ my: 1 }} />
          <Tabs value={0} indicatorColor="secondary" textColor="secondary" variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            <Tab label="Convocatoria" sx={{ textTransform: 'none' }} />
            <Tab label="Registro" sx={{ textTransform: 'none' }} />
            <Tab label="Resultados" sx={{ textTransform: 'none' }} />
          </Tabs>
          <Typography variant="body2" color="textSecondary">
            El contenido real de la página del evento usará estos colores y datos.
          </Typography>

          {/* *** SECCIÓN CONTACTOS ACTUALIZADA *** */}
          {Array.isArray(contactos) && contactos.filter(c => c.nombre || c.email || c.telefono).length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}> {/* Más margen superior */}
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}> {/* Título más pequeño */}
                Contactos del Evento
              </Typography>
              {/* Iterar sobre los contactos */}
              {contactos.map((contacto, index) => (
                 // Renderizar solo si el contacto tiene algún dato
                 (contacto.nombre || contacto.email || contacto.telefono) && (
                  <Box key={index} sx={{ mb: 1.5 }}> {/* Espacio entre contactos */}
                    {/* Nombre con Icono */}
                    {contacto.nombre && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{contacto.nombre}</Typography>
                      </Box>
                    )}
                    {/* Email con Icono y Link */}
                    {contacto.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3.5 }}> {/* Indentación */}
                        <EmailIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize:'0.9rem' }} />
                        <Link href={`mailto:${contacto.email}`} variant="body2" color="textSecondary" underline="hover">
                          {contacto.email}
                        </Link>
                      </Box>
                    )}
                    {/* Teléfono con Icono y Link */}
                    {contacto.telefono && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3.5, mt: 0.5 }}> {/* Indentación */}
                        <PhoneIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize:'0.9rem' }} />
                        <Link href={`tel:${contacto.telefono}`} variant="body2" color="textSecondary" underline="hover">
                          {contacto.telefono}
                        </Link>
                      </Box>
                    )}
                  </Box>
                 )
              ))}
            </Box>
          )}
          {/* *** FIN SECCIÓN CONTACTOS *** */}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

// Definir PropTypes
EventPagePreview.propTypes = {
  colorPrimario: PropTypes.string,
  colorSecundario: PropTypes.string,
  nombreEvento: PropTypes.string,
  subtitulo: PropTypes.string,
  lugar: PropTypes.string,
  fechaInicio: PropTypes.string,
  fechaFin: PropTypes.string,
  logoSrc: PropTypes.string,
  portadaSrc: PropTypes.string,
  redesSociales: PropTypes.shape({
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    twitter: PropTypes.string,
    whatsapp: PropTypes.string,
    sitioWeb: PropTypes.string, // Corregido nombre
  }),
  contactos: PropTypes.arrayOf(PropTypes.shape({
    nombre: PropTypes.string,
    email: PropTypes.string,
    telefono: PropTypes.string,
  })),
};

export default EventPagePreview;